import { useCallback, useEffect, useMemo, useState } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { brainsEndpoints as endpoints, dialogsEndpoints, versionsEndpoints } from '@/api/endpoints';
import { callDelete, callGet, callPatch, callPost, callPut } from '@/api/fetcher';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { Brain, BrainFromLogs, Brains, BrainGuidelines, PartialBrain } from '@/shared/types/brain';
import { Node } from '@/shared/types/node';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { walkAuthSubAgentConfigs } from '@/util/auth/walkAuthSubAgentConfigs';
import { exportBrain as downloadBrain } from '@/util/fileManager';
import { isLimitReached } from '@/util/limits';

import { useAccount } from './useAccount';
import useHomeCheckList, { AccountUserPrefsEnum } from './useHomeCheckList';
import { createEntityCache } from './utils/entityCache';

const ONE_SECOND = 1000;
const TEN_SECONDS = 10 * ONE_SECOND;
const THIRTY_SECONDS = 30 * ONE_SECOND;

export const API = Object.freeze({
  listBrains: async (): Promise<Brains> => callGet(endpoints.brains),

  getBrain: async (brainId: string): Promise<Brain> => callGet(endpoints.brain(brainId)),

  exportBrain: async (brainId: string): Promise<Brain> =>
    callGet(`${endpoints.brain(brainId)}?export=true`),

  createBrain: async (newBrain: Partial<Brain>): Promise<Brain> =>
    callPost(endpoints.brains, newBrain),

  updateBrain: async ({ brain_id, ...brain }: PartialBrain): Promise<Brain> =>
    callPut(endpoints.brain(brain_id), brain),

  patchBrain: async ({ brain_id, ...brain }: PartialBrain): Promise<Brain> =>
    callPatch(endpoints.brain(brain_id), brain),

  patchBrainGuidelines: async ({
    brain_id,
    ...guidelines
  }: { brain_id: string } & BrainGuidelines): Promise<Brain> =>
    callPatch(`${endpoints.brain(brain_id)}/guidelines`, guidelines),

  deleteBrain: async (brainId: string, options?: { force?: boolean }): Promise<Brain> => {
    const url = options?.force
      ? `${endpoints.brain(brainId)}?force=true`
      : endpoints.brain(brainId);
    return callDelete(url);
  },

  createBrainFromLogs: async (newBrain: BrainFromLogs): Promise<Brain> =>
    callPost(endpoints.onboarding, newBrain),

  getTemplate: async (lang: string): Promise<Brain> => callGet(endpoints.templates(lang)),
});

export const brainCache = createEntityCache<Brain, 'brains'>({
  listKey: () => [endpoints.brains],
  detailKey: (_parentId, brainId) => [endpoints.brain(brainId)],
  listField: 'brains',
  getId: (brain) => brain.brain_id,
  afterChange: (queryClient, change) => {
    // The versions diff compares against the live brain, so any edit makes it stale
    if (change.type === 'updated') {
      queryClient.invalidateQueries({ queryKey: [versionsEndpoints.diff(change.entity.brain_id)] });
    }
  },
});

/**
 * Updates brain data in cache using deep merge for partial updates.
 * This is specifically designed for PATCH operations where only updated fields are returned.
 */
export const onBrainGuidelinesPatched = (
  queryClient: QueryClient,
  patchedGuidelines: Partial<Brain> & { brain_id: string }
) => {
  queryClient.setQueryData<Brain>([endpoints.brain(patchedGuidelines.brain_id)], (prev: Brain) => ({
    ...prev,
    guidelines: { ...prev?.guidelines, ...patchedGuidelines },
  }));

  const queryKey = [endpoints.brains];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<Brains>(queryKey, (prev: Brains) => ({
      brains: (prev?.brains || []).map((item) =>
        item.brain_id === patchedGuidelines.brain_id
          ? { ...item, guidelines: { ...item.guidelines, ...patchedGuidelines } }
          : item
      ),
    }));
  }
};

const useBrains = (brainId?: string) => {
  const { t } = useTranslation();

  const translateKey = 'ai_agents';
  const [refetchIntervall, setRefetchIntervall] = useState(THIRTY_SECONDS);

  const canRead = useSelector((state: RootState) => getPermissions(state, 'brains', actions.READ));
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);
  const { account } = useAccount();
  const { markAsComplete } = useHomeCheckList();

  const { data: brains, status: listStatus } = useQuery<Brains, Error>({
    queryKey: [endpoints.brains],
    queryFn: () => API.listBrains(),
    enabled: !!accountId && canRead,
    staleTime: THIRTY_SECONDS * 2, // 1 minute
  });

  const {
    data: brain,
    status: getStatus,
    isLoading: isBrainLoading,
    error: brainError,
  } = useQuery<Brain, Error>({
    queryKey: [endpoints.brain(brainId)],
    queryFn: () => API.getBrain(brainId),
    enabled: !!brainId && canRead,
    refetchInterval: refetchIntervall,
    staleTime: THIRTY_SECONDS * 2, // 1 minute
  });

  useEffect(() => {
    if (brainError) {
      console.error(brainError, 'Error loading brain');
      setRefetchIntervall(THIRTY_SECONDS);
    }
  }, [brainError, setRefetchIntervall]);

  useEffect(() => {
    if (brain?.status) {
      if (brain.status === 'training') {
        setRefetchIntervall(ONE_SECOND);
      } else if (brain.status === 'unavailable') {
        setRefetchIntervall(TEN_SECONDS);
      } else {
        setRefetchIntervall(THIRTY_SECONDS);
      }
    }
  }, [brain?.status]);

  const {
    mutate: updateBrain,
    mutateAsync: updateBrainAsync,
    status: updateStatus,
  } = useMutation<Brain, Error, PartialBrain & { preventToast?: boolean }>({
    mutationFn: ({ preventToast: _, ...rest }) => API.updateBrain(rest),
    onSuccess: (resp, variables) => {
      brainCache.updated(queryClient, resp);

      if (!variables.preventToast) {
        toast.success(t(`${translateKey}.updated`, { 0: resp.name }));
      }

      // Only mark as complete if a collection_id was set in the update
      if (variables.collection_id) {
        markAsComplete(AccountUserPrefsEnum.CONNECT_BRAIN);
      }
      // Mark as complete if guidelines were configured
      if (variables.guidelines) {
        markAsComplete(AccountUserPrefsEnum.CONFIGURE_GUIDELINES);
      }
    },
    meta: { errorToast: true },
  });

  const {
    mutate: patchBrainGuidelines,
    mutateAsync: patchBrainGuidelinesAsync,
    status: patchBrainGuidelinesStatus,
  } = useMutation<
    Brain,
    Error,
    { brain_id: string; preventToast?: boolean } & Partial<BrainGuidelines>
  >({
    mutationFn: ({ preventToast: _, ...rest }) => API.patchBrainGuidelines(rest),
    onSuccess: (resp, variables) => {
      // Use deep merge for patch operations since backend only returns updated fields
      onBrainGuidelinesPatched(queryClient, { ...resp, brain_id: variables.brain_id });

      if (!variables.preventToast) {
        toast.success(t(`${translateKey}.updated`, { 0: resp?.name || brain?.name }));
      }
    },
    meta: { errorToast: true },
  });

  const { mutate: createBrain, status: createStatus } = useMutation<Brain, Error, Partial<Brain>>({
    mutationFn: API.createBrain,
    meta: {
      errorToast: true,
      segment: {
        success: EventName.AIAgentCreated,
        action: 'ai_agent_create',
        props: { source: 'import' },
        successProps: (brain: Brain) => ({ brain_id: brain.brain_id }),
      },
    },
    onSuccess: (resp) => {
      brainCache.created(queryClient, resp);
      toast.success(t(`${translateKey}.created`, { 0: resp?.name }));
    },
  });

  const {
    mutate: deleteBrain,
    mutateAsync: deleteBrainAsync,
    status: deleteStatus,
  } = useMutation<Brain, Error, { brainId: string; options?: { force?: boolean } }>({
    mutationFn: ({ brainId, options }) => API.deleteBrain(brainId, options),
    onSuccess: (resp) => {
      brainCache.removed(queryClient, { id: resp.brain_id });
      toast.success(t(`${translateKey}.deleted`));
    },
    meta: { errorToast: true },
  });

  // Memoize the exportBrain function to prevent unnecessary re-renders
  const exportBrain = useCallback(
    async (id: string) => {
      const eBrain = await callGet(`/www/api/v1/brains/${id}?export=true`);
      downloadBrain(eBrain);
      toast.success(t(`${translateKey}.exported`, { 0: eBrain.name }));
    },
    [t, translateKey]
  );

  // Memoize the getTemplate function to prevent unnecessary re-renders
  const getTemplate = useCallback((lang: string) => API.getTemplate(lang), []);

  const brainsByLanguage = useCallback(
    (language: string) => {
      return brains?.brains?.filter((b) => b.language === language) || [];
    },
    [brains?.brains]
  );

  const hasSellerBrain = useMemo(
    () => brains?.brains?.some((brain) => brain.brain_type !== 'support'),
    [brains?.brains]
  );

  const activeSessions = useMemo(() => {
    return brain?.active_sessions || 0;
  }, [brain?.active_sessions]);

  // Memoize computed values to prevent unnecessary re-calculations
  const maxBrains = useMemo(() => account?.max_brains, [account?.max_brains]);
  const maxBrainsReached = useMemo(
    () => isLimitReached(brains?.brains?.length ?? 0, account?.max_brains),
    [brains?.brains?.length, account?.max_brains]
  );

  const insightsEnabled = brain?.metadata?.insights_enabled ?? false;

  const improvementsEnabled = brain?.metadata?.improvements_enabled ?? false;

  const isWebhookUsedInAuth = useCallback(
    (webhookId: string): boolean => {
      const dialogs = queryClient.getQueryData<{ dialogs: { dialog_id: string; nodes: Node[] }[] }>(
        [dialogsEndpoints.dialogs(brainId)]
      );
      const authConfigs = walkAuthSubAgentConfigs(dialogs?.dialogs);
      return authConfigs.some(({ config }) => {
        if (
          config.identification_step?.pre_enter_webhook_id === webhookId ||
          config.verification_step?.pre_enter_webhook_id === webhookId
        ) {
          return true;
        }
        const questions = [
          ...(config.identification_step?.questions ?? []),
          ...(config.verification_step?.questions ?? []),
        ];
        return questions.some(
          (q) => q.validation?.method === 'webhook' && q.validation.webhook_id === webhookId
        );
      });
    },
    [brainId, queryClient]
  );

  return {
    brains: brains?.brains,
    brain,
    isBrainLoading,
    maxBrains,
    maxBrainsReached,
    getStatus,
    listStatus,
    createStatus,
    deleteStatus,
    updateStatus,
    createBrain,
    deleteBrain,
    deleteBrainAsync,
    updateBrain,
    updateBrainAsync,
    patchBrainGuidelines,
    patchBrainGuidelinesAsync,
    patchBrainGuidelinesStatus,
    exportBrain,
    brainsByLanguage,
    getTemplate,
    hasSellerBrain,
    activeSessions,
    insightsEnabled,
    improvementsEnabled,
    isWebhookUsedInAuth,
  };
};

export default useBrains;
